import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";

const IconHome = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
    <path d="M3 11.5L12 4l9 7.5" />
    <path d="M6.5 10.5V20h11V10.5" />
  </svg>
);

const IconShop = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
    <path d="M4 8h16l-1.2 11.2a2 2 0 0 1-2 1.8H7.2a2 2 0 0 1-2-1.8L4 8z" />
    <path d="M8 8a4 4 0 0 1 8 0" />
  </svg>
);

const IconContact = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
    <path d="M22 16.9v2a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h2a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L7.1 9.5a16 16 0 0 0 6 6l1.1-1.1a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" />
  </svg>
);

export default function Header() {
  const { count } = useCart();
  const { lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const search = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/shop?q=${encodeURIComponent(q.trim())}` : "/shop");
    setOpen(false);
  };

  return (
    <header className={`site-header ${scrolled ? "scrolled" : ""}`}>
      <div className="header-shell">
        <Link className="brand" to="/" onClick={() => setOpen(false)}>
          <img className="brand-mark" src="/ethiopian-flag.svg" alt="" width="32" height="32" />
          <span className="brand-text">Aki Mart</span>
        </Link>

        <div className={`header-center ${open ? "open" : ""}`}>
          <nav className="nav-main" aria-label="Primary">
            <NavLink to="/" end onClick={() => setOpen(false)}>
              <IconHome />
              <span>Home</span>
            </NavLink>
            <NavLink to="/shop" onClick={() => setOpen(false)}>
              <IconShop />
              <span>Shop</span>
            </NavLink>
            <NavLink to="/contact" onClick={() => setOpen(false)}>
              <IconContact />
              <span>Contact</span>
            </NavLink>
          </nav>

          <a className="nav-phone" href="tel:+14044536512" aria-label="Call Aki Mart">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
              <path d="M22 16.9v2a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h2a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L7.1 9.5a16 16 0 0 0 6 6l1.1-1.1a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" />
            </svg>
            <span>+1 (404) 453-6512</span>
          </a>

          <button
            type="button"
            className="lang-toggle"
            onClick={() => setLang(lang === "en" ? "am" : "en")}
            aria-label="Toggle language"
            style={{
              background: "transparent",
              border: "1px solid var(--line)",
              borderRadius: "999px",
              padding: "0.25rem 0.65rem",
              fontSize: "0.75rem",
              fontWeight: "600",
              cursor: "pointer",
              color: "var(--forest-deep)",
              transition: "0.2s"
            }}
          >
            {lang === "en" ? "AM" : "EN"}
          </button>

          <form className="search-form" onSubmit={search} role="search">
            <label className="sr-only" htmlFor="q">
              Search products
            </label>
            <input
              id="q"
              type="search"
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button type="submit" aria-label="Search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3-3" />
              </svg>
            </button>
          </form>
        </div>

        <div className="header-end">
          <Link className="cart-btn" to="/cart" aria-label={`Cart (${count} items)`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M6 6h15l-1.5 9h-12z" />
              <circle cx="9" cy="20" r="1.5" />
              <circle cx="18" cy="20" r="1.5" />
              <path d="M6 6L5 3H2" />
            </svg>
            {count > 0 && <span className="cart-count">{count}</span>}
          </Link>

          <button
            className="nav-toggle"
            type="button"
            aria-expanded={open}
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
          </button>
        </div>
      </div>
    </header>
  );
}
